import { useEffect } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import BarraSync from './BarraSync'
import AvisoFalhaGravacao from './AvisoFalhaGravacao'
import AlertaAtrasados from './AlertaAtrasados'
import AlertaAviso from './AlertaAviso'
import { LogoMarca } from './LogoMarca'
import Sininho from './Sininho'
import { useLiveQuery } from 'dexie-react-hooks'
import { buscarEmpresa } from '../empresa/empresa'
import { logoValido } from '../lib/imagem'
import { iniciarSyncAutomatico } from '../sync/sincronizador'
import { mostraVoltar, destinoDoVoltar } from './voltar'
import { podeSair } from './guardaDeSaida'
import { useAuth } from '../auth/AuthProvider'
import { nomeDeExibicao } from '../lib/nomeDeExibicao'
import { iniciaisDoNome } from '../lib/iniciais'
import { dataDoTopo } from '../compromissos/regras'
import { useAgora } from '../lib/useAgora'

// A Agenda e a segunda porque e a tela que o representante abre toda manha.
// Produtos e Fornecedoras sairam daqui para a pagina Mais: cabiam cinco abas, e
// a sexta deixaria os rotulos ilegiveis num celular de 360px.
// ⚠ Mexer aqui exige mexer em RAIZES, no voltar.ts -- ha teste que cobra.
const abas = [
  { para: '/', rotulo: 'Início' },
  { para: '/agenda', rotulo: 'Rota' },
  { para: '/clientes', rotulo: 'Clientes' },
  { para: '/pedidos', rotulo: 'Pedidos' },
  { para: '/mais', rotulo: 'Mais' },
]

// Desenho dela 07/10: icone por cima do rotulo; a aba ativa vem com o icone
// cheio e um traco embaixo. Cheio = forma pintada, detalhes vazados em branco.
function IconeDaAba({ para, cheio }: { para: string; cheio: boolean }) {
  const forma = cheio ? 'currentColor' : 'none'
  const detalhe = cheio ? 'white' : 'currentColor'
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.8}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {para === '/' && (
        <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" fill={forma} />
      )}
      {para === '/agenda' && (
        <>
          <rect x="3" y="4.5" width="18" height="16.5" rx="2.5" fill={forma} />
          <path d="M8 2.5v4M16 2.5v4" />
          <path d="M3 9.5h18" stroke={detalhe} />
          <path d="M8 13.5h.01M12 13.5h.01M16 13.5h.01M8 17h.01M12 17h.01" stroke={detalhe} strokeWidth={2.4} />
        </>
      )}
      {para === '/clientes' && (
        <>
          <circle cx="12" cy="8" r="4" fill={forma} />
          <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1z" fill={forma} />
        </>
      )}
      {para === '/pedidos' && (
        <>
          <path d="M14 2.5H6.5a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V8z" fill={forma} />
          <path d="M14 2.5V8h5.5" />
          <path d="M8.5 13h7M8.5 17h5" stroke={detalhe} />
        </>
      )}
      {para === '/mais' && (
        <path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth={cheio ? 4 : 3.2} />
      )}
    </svg>
  )
}

export default function Layout() {
  useEffect(() => iniciarSyncAutomatico(), [])
  const local = useLocation()
  const navegar = useNavigate()
  const { session } = useAuth()
  const noInicio = local.pathname === '/'
  const usuarioId = session?.user.id
  const empresa = useLiveQuery(() => buscarEmpresa(usuarioId), [usuarioId])
  const agora = useAgora()

  async function voltar() {
    // ⚠ Achado dela no 2º teste real, 03/09: sem isto, quem tocasse aqui com
    // um rascunho aberto na ficha perdia o que tinha digitado sem aviso
    // nenhum. A tela funda registra a pergunta; o Layout so obedece.
    if (!(await podeSair())) return
    if (destinoDoVoltar(local.key) === 'inicio') navegar('/')
    else navegar(-1)
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      {/* Desenho dela 04/10: fundo verde-esmeralda escuro com brilho na diagonal,
          logo (grafico + seta dourada) e "CRM Multimarca". Completo no Inicio,
          faixa baixa (logo + nome) nas outras telas -- decisao dela. */}
      <header className={`relative overflow-hidden text-white fundo-topo ${noInicio ? 'px-4 pt-4 pb-4 space-y-4' : 'px-3 py-2'}`}>
        {/* fio de ouro velho em curva no canto, como no print */}
        <svg aria-hidden="true" viewBox="0 0 200 100" preserveAspectRatio="none" className="pointer-events-none absolute right-0 top-0 h-full w-1/2">
          <path d="M40 0 C 90 10, 150 30, 200 85" fill="none" stroke="#c9a24a" strokeOpacity="0.55" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
        </svg>
        <div className="relative flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {mostraVoltar(local.pathname) && (
            <button
              onClick={voltar}
              data-testid="voltar"
              aria-label="Voltar"
              className="-ml-1 px-1 text-2xl leading-none shrink-0"
            >
              ‹
            </button>
          )}
          <LogoMarca className={`sombra-3d shrink-0 ${noInicio ? 'w-12 h-12' : 'w-8 h-8'}`} />
          <div className="min-w-0 leading-none">
            <h1 className={`sombra-3d truncate ${noInicio ? 'text-[22px]' : 'text-base'}`}>
              <span className="font-black tracking-tight">CR<span className="texto-ouro">M</span></span>
              {noInicio ? <br /> : ' '}
              <span className="font-light">Multimarca</span>
            </h1>
            {/* Spec 2026-09-29 (tela Inicio), item 1: so na tela Inicio. */}
            {noInicio && <p className="sombra-3d mt-0.5 text-[8px] uppercase tracking-[0.1em] text-white/80 truncate">Representante Comercial</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <BarraSync />
          <Sininho grande={noInicio} />
          {noInicio && (
            <Link to="/perfil" aria-label="Meu perfil" data-testid="avatar-perfil"
              className="w-9 h-9 rounded-full bg-white/15 ring-1 ring-white/30 flex items-center justify-center text-sm font-bold overflow-hidden">
              {/* Spec 2026-10-06: a imagem da empresa INTEIRA, fundo branco. */}
              {logoValido(empresa?.logo)
                ? <img src={empresa!.logo!} alt="" data-testid="avatar-imagem" className="w-full h-full object-contain bg-white" />
                : iniciaisDoNome(nomeDeExibicao(session?.user))}
            </Link>
          )}
        </div>
        </div>
        {/* Spec 2026-10-02 (ciclo 2A): a saudacao subiu para o verde, com a
            data do dia so para ler. Substitui as 2 frases do Painel. */}
        {noInicio && (
          <div className="relative flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p data-testid="saudacao" className="sombra-3d text-lg font-bold truncate">
                Olá{nomeDeExibicao(session?.user) ? <>, <span className="texto-ouro">{nomeDeExibicao(session?.user)}</span></> : ''}!
              </p>
              <p className="text-xs text-white/80">Um ótimo dia de vendas!</p>
            </div>
            <span data-testid="data-do-topo" className="shrink-0 rounded-xl border border-white/25 bg-black/25 px-2.5 py-1.5 text-xs font-semibold">
              📅 {dataDoTopo(agora)}
            </span>
          </div>
        )}
      </header>
      <AvisoFalhaGravacao />
      <AlertaAviso />
      <AlertaAtrasados />
      <main className="flex-1 overflow-y-auto pb-20">
        <Outlet />
      </main>
      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 flex">
        {abas.map((aba) => (
          <NavLink
            key={aba.para}
            to={aba.para}
            end={aba.para === '/'}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-0.5 pt-2 pb-1.5 text-xs ${isActive ? 'text-teal-700 font-bold' : 'text-slate-500'}`
            }
          >
            {({ isActive }) => (
              <>
                <IconeDaAba para={aba.para} cheio={isActive} />
                <span>{aba.rotulo}</span>
                <span className={`h-1 w-10 rounded-full ${isActive ? 'bg-teal-700' : 'bg-transparent'}`} />
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
